// src/app/pages/msg/components/RoleSelector.tsx
"use client";

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useEffect, useState } from "react";
import { useTenancy } from "@/contexts/TenancyContext";
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '@/js/external-js/config/main_config';

interface RoleSelectorProps {
    standard: "iso27701" | "iso42001";
    settingsKey: string; // New required prop
    value?: string;
    onChange?: (value: string) => void;
    className?: string;
    readOnly?: boolean;
}

const STANDARDS = {
    iso27701: {
        label: "ISO/IEC 27701:2019",
        placeholder: "Select PII Role",
        roles: [
            { value: "controller", label: "PII Controller" },
            { value: "joint-controller", label: "Joint PII Controller" },
            { value: "processor", label: "PII Processor" },
            { value: "controller-and-processor", label: "Controller & Processor" },
            { value: "not-applicable", label: "Not Applicable" }
        ]
    },
    iso42001: {
        label: "ISO/IEC 42001:2023",
        placeholder: "Select AI Role",
        roles: [
            { value: "provider", label: "AI Provider" },
            { value: "producer", label: "AI Producer" },
            { value: "customer", label: "AI Customer" },
            { value: "partner", label: "AI Partner" },
            { value: "subject", label: "AI Subject" },
            { value: "not-applicable", label: "Not Applicable" }
        ]
    }
};

export function RoleSelector({ standard, settingsKey, value, onChange, className = "", readOnly = false }: RoleSelectorProps) {
    const { activeTenancy } = useTenancy();
    const [selectedValue, setSelectedValue] = useState(value || "");

    useEffect(() => {
        const loadSavedRole = async () => {
            if (activeTenancy?.id && !value) {
                try {
                    const savedRole = await loadRoleFromDatabase(activeTenancy.id, standard, settingsKey);
                    if (savedRole) {
                        setSelectedValue(savedRole);
                        onChange?.(savedRole);
                    }
                } catch (error) {
                    console.error('Error loading saved role:', error);
                }
            }
        };

        loadSavedRole();
    }, [activeTenancy?.id, standard, settingsKey, value, onChange]);

    const handleChange = (newValue: string) => {
        setSelectedValue(newValue);
        if (onChange) {
            onChange(newValue);
        }
        // Save to database
        if (activeTenancy?.id) {
            saveRoleToDatabase(activeTenancy.id, standard, settingsKey, newValue);
        }
    };

    const config = STANDARDS[standard];

    return (
        <div className={`inline-flex items-center gap-2 ${className}`}>
            <Select
                value={selectedValue}
                onValueChange={handleChange}
                disabled={readOnly}
            >
                <SelectTrigger className="w-auto min-w-[180px] bg-background border-border/50 hover:bg-accent/10 transition-colors">
                    <SelectValue placeholder={config.placeholder} />
                </SelectTrigger>
                <SelectContent>
                    {config.roles.map((role) => (
                        <SelectItem key={role.value} value={role.value}>
                            {role.label}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </div>
    );
}

// Helper function to save the role to your database
async function saveRoleToDatabase(tenancyId: string, standard: string, settingsKey: string, role: string) {
    try {
        const docPath = `customers/${tenancyId}/companySettings/complianceModules`;
        const docRef = doc(db, docPath);
        const timestamp = new Date().toISOString();

        // First get the existing document to merge with
        const docSnap = await getDoc(docRef);
        const existingData = docSnap.exists() ? docSnap.data() : {};

        // Update the specific standard's settings
        await setDoc(docRef, {
            ...existingData,
            [standard]: {
                ...(existingData[standard] || {}),
                [settingsKey]: role,
                updatedAt: timestamp
            }
        }, { merge: true });

        console.log(`[RoleSelector] Saved ${standard} role for ${settingsKey}:`, role);
    } catch (error) {
        console.error(`[RoleSelector] Error saving ${standard} role:`, error);
        throw error;
    }
}

// Helper function to load the role from your database
export async function loadRoleFromDatabase(tenancyId: string, standard: string, settingsKey: string): Promise<string | null> {
    try {
        const docPath = `customers/${tenancyId}/companySettings/complianceModules`;
        const docSnap = await getDoc(doc(db, docPath));

        if (docSnap.exists()) {
            return docSnap.data()?.[standard]?.[settingsKey] || null;
        }
        return null;
    } catch (error) {
        console.error(`[RoleSelector] Error loading ${standard} role:`, error);
        return null;
    }
}
